<div class="container-fluid py-3">

  <!-- Header -->
  <div class="d-flex justify-content-between align-items-center mb-3">
    <div class="d-flex align-items-center gap-2">
      <button class="btn btn-sm btn-outline-secondary" (click)="goBack()" [title]="'General.back' | translate">
        <i class="bi bi-arrow-left" aria-hidden="true"></i>
      </button>
      <h4 class="mb-0">
        <i class="bi bi-journal-code me-2"></i>{{ 'ApiErrorLog.title' | translate }}
      </h4>
    </div>
    <div class="d-flex align-items-center gap-2">
      <small class="text-muted" *ngIf="totalCount > 0">
        {{ totalCount }} {{ 'ApiErrorLog.entries' | translate }}
      </small>
      <button class="btn btn-sm btn-outline-primary" (click)="loadData()" [disabled]="loading">
        <i class="bi bi-arrow-clockwise" [class.spin]="loading"></i>
      </button>
    </div>
  </div>

  <!-- Filters -->
  <div class="card mb-3">
    <div class="card-body py-2">
      <div class="row g-2 align-items-end">
        <div class="col-md-2">
          <label class="form-label form-label-sm mb-1">{{ 'ApiErrorLog.fromDate' | translate }}</label>
          <input type="date" class="form-control form-control-sm" [(ngModel)]="fromDate" />
        </div>
        <div class="col-md-2">
          <label class="form-label form-label-sm mb-1">{{ 'ApiErrorLog.toDate' | translate }}</label>
          <input type="date" class="form-control form-control-sm" [(ngModel)]="toDate" />
        </div>
        <div class="col-md-2">
          <label class="form-label form-label-sm mb-1">{{ 'ApiErrorLog.level' | translate }}</label>
          <select class="form-select form-select-sm" [(ngModel)]="selectedLevel">
            <option value="">{{ 'ApiErrorLog.allLevels' | translate }}</option>
            <option *ngFor="let level of levels" [value]="level">{{ level }}</option>
          </select>
        </div>
        <div class="col-md-2">
          <label class="form-label form-label-sm mb-1">Kunde-domene</label>
          <input type="text" class="form-control form-control-sm"
                 placeholder="f.eks. ihk.eportal.no"
                 [(ngModel)]="selectedDomain"
                 (keyup.enter)="onSearch()" />
        </div>
        <div class="col-md-3">
          <label class="form-label form-label-sm mb-1">{{ 'ApiErrorLog.search' | translate }}</label>
          <input type="text" class="form-control form-control-sm"
                 [placeholder]="'ApiErrorLog.searchPlaceholder' | translate"
                 [(ngModel)]="searchText"
                 (keyup.enter)="onSearch()" />
        </div>
        <div class="col-md-3 d-flex gap-2">
          <button class="btn btn-sm btn-primary" (click)="onSearch()" [disabled]="loading">
            <i class="bi bi-search me-1"></i>{{ 'ApiErrorLog.searchBtn' | translate }}
          </button>
          <button class="btn btn-sm btn-outline-secondary" (click)="resetFilters()">
            <i class="bi bi-x-circle me-1"></i>{{ 'ApiErrorLog.reset' | translate }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Loading -->
  <div *ngIf="loading && items.length === 0" class="text-center py-5">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">{{ 'General.loading' | translate }}</span>
    </div>
  </div>

  <!-- Empty state -->
  <div *ngIf="!loading && items.length === 0" class="text-center py-5">
    <i class="bi bi-journal-check fs-1 text-muted"></i>
    <p class="text-muted mt-2">{{ 'ApiErrorLog.noEntries' | translate }}</p>
  </div>

  <!-- Results table -->
  <div *ngIf="items.length > 0" class="table-responsive">
    <table class="table table-sm table-hover mb-0 log-table">
      <thead class="table-light">
        <tr>
          <th style="width: 5%"></th>
          <th style="width: 8%">{{ 'ApiErrorLog.level' | translate }}</th>
          <th style="width: 14%">{{ 'ApiErrorLog.timestamp' | translate }}</th>
          <th>{{ 'ApiErrorLog.message' | translate }}</th>
          <th style="width: 5%" class="text-center">
            <i class="bi bi-bug" [title]="'ApiErrorLog.hasException' | translate"></i>
          </th>
        </tr>
      </thead>
      <tbody>
        <ng-container *ngFor="let item of items">
          <!-- Summary row -->
          <tr class="cursor-pointer log-row" [class.log-row-expanded]="expandedRow === item.id"
              [class.table-danger]="item.level === 'Error' || item.level === 'Fatal'"
              [class.table-warning]="item.level === 'Warning'"
              (click)="toggleDetail(item)">
            <td class="text-center">
              <i class="bi" [class.bi-chevron-down]="expandedRow === item.id" [class.bi-chevron-right]="expandedRow !== item.id"></i>
            </td>
            <td>
              <span class="badge" [ngClass]="getLevelBadgeClass(item.level)">{{ item.level }}</span>
            </td>
            <td><small>{{ formatTimestamp(item.timeStamp) }}</small></td>
            <td class="text-truncate" style="max-width: 500px;" [title]="item.message || ''">{{ item.message }}</td>
            <td class="text-center">
              <i *ngIf="item.hasException" class="bi bi-bug-fill text-danger"></i>
            </td>
          </tr>

          <!-- Detail row -->
          <tr *ngIf="expandedRow === item.id" class="detail-row">
            <td colspan="5">
              <div class="detail-panel p-3">

                <!-- Loading detail -->
                <div *ngIf="loadingDetail" class="text-center py-3">
                  <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                </div>

                <div *ngIf="selectedEntry && !loadingDetail">
                  <!-- Full message -->
                  <div class="detail-section">
                    <h6 class="detail-label">{{ 'ApiErrorLog.message' | translate }}</h6>
                    <pre class="detail-pre">{{ selectedEntry.message }}</pre>
                  </div>

                  <!-- Message template -->
                  <div *ngIf="selectedEntry.messageTemplate" class="detail-section">
                    <h6 class="detail-label">{{ 'ApiErrorLog.messageTemplate' | translate }}</h6>
                    <pre class="detail-pre detail-pre-muted">{{ selectedEntry.messageTemplate }}</pre>
                  </div>

                  <!-- Exception -->
                  <div *ngIf="selectedEntry.exception" class="detail-section">
                    <h6 class="detail-label text-danger">
                      <i class="bi bi-bug-fill me-1"></i>{{ 'ApiErrorLog.exception' | translate }}
                    </h6>
                    <pre class="detail-pre detail-pre-danger">{{ selectedEntry.exception }}</pre>
                  </div>

                  <!-- Properties XML -->
                  <div *ngIf="selectedEntry.properties" class="detail-section">
                    <h6 class="detail-label">{{ 'ApiErrorLog.properties' | translate }}</h6>
                    <pre class="detail-pre detail-pre-xml">{{ formatPropertiesXml(selectedEntry.properties) }}</pre>
                  </div>

                  <!-- LogEvent JSON -->
                  <div *ngIf="selectedEntry.logEvent" class="detail-section">
                    <h6 class="detail-label">{{ 'ApiErrorLog.logEvent' | translate }}</h6>
                    <pre class="detail-pre detail-pre-json">{{ formatLogEventJson(selectedEntry.logEvent) }}</pre>
                  </div>

                  <!-- Metadata -->
                  <div class="detail-meta d-flex justify-content-between align-items-center">
                    <small class="text-muted">
                      ID: {{ selectedEntry.id }} &middot;
                      {{ 'ApiErrorLog.level' | translate }}: {{ selectedEntry.level }} &middot;
                      {{ formatTimestamp(selectedEntry.timeStamp) }}
                    </small>
                    <div class="d-flex gap-2">
                      <button class="btn btn-sm btn-outline-primary" (click)="copyDebugPrompt(selectedEntry)" [title]="'ApiErrorLog.generatePromptTooltip' | translate">
                        <i class="bi bi-stars me-1" aria-hidden="true"></i>{{ 'ApiErrorLog.generatePrompt' | translate }}
                      </button>
                    </div>
                  </div>
                  <!-- Prompt preview -->
                  <div *ngIf="generatedPrompt && expandedRow === selectedEntry.id" class="detail-section mt-2">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <h6 class="detail-label mb-0">
                        <i class="bi bi-stars me-1"></i>{{ 'ApiErrorLog.promptReady' | translate }}
                      </h6>
                      <button class="btn btn-sm btn-outline-secondary" (click)="generatedPrompt = null">
                        <i class="bi bi-x-lg" aria-hidden="true"></i>
                      </button>
                    </div>
                    <pre class="detail-pre detail-pre-prompt">{{ generatedPrompt }}</pre>
                    <small class="text-success"><i class="bi bi-check-circle me-1"></i>{{ 'ApiErrorLog.promptCopied' | translate }}</small>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </ng-container>
      </tbody>
    </table>
  </div>

  <!-- Pagination -->
  <div *ngIf="totalPages > 1" class="d-flex justify-content-between align-items-center mt-3">
    <div class="d-flex align-items-center gap-2">
      <small class="text-muted">{{ 'ApiErrorLog.rowsPerPage' | translate }}:</small>
      <select class="form-select form-select-sm" style="width: auto;" [(ngModel)]="pageSize" (change)="onPageSizeChange(pageSize)">
        <option [ngValue]="25">25</option>
        <option [ngValue]="50">50</option>
        <option [ngValue]="100">100</option>
        <option [ngValue]="200">200</option>
      </select>
    </div>
    <ngb-pagination size="sm"
                    [collectionSize]="totalCount"
                    [(page)]="pageNumber"
                    [pageSize]="pageSize"
                    [maxSize]="5"
                    [boundaryLinks]="true"
                    (pageChange)="onPageChange($event)">
    </ngb-pagination>
  </div>

</div>
